<template>
  <teleport to="body">
    <div v-if="visible" class="preview-wrapper" role="dialog" aria-modal="true">
      <div class="preview-backdrop" @click="onBackdropClick"></div>
      <div :class="['preview-container', { 'pdf-full': isPDF }]">
        <div class="modal-content modal-content-fullscreen">
          <div class="modal-header">
            <h3 class="modal-title">{{ name || 'Previsualización' }}</h3>
            <button
              type="button"
              class="btn-close"
              @click="$emit('close')"
              aria-label="Close"
            >
              <span class="material-icons action-icon">close</span>
            </button>
          </div>
          <div class="modal-body">
            <!-- spinner mientras se descarga/crea la URL -->
            <div v-if="loading" style="display:flex;align-items:center;justify-content:center;width:100%;">
              <div class="loader" aria-hidden="true">Cargando...</div>
            </div>
            <img v-if="isImage && previewUrl" :src="previewUrl" :alt="name" class="preview-media preview-image" />
            <iframe v-else-if="isPDF && previewUrl" :src="previewUrl" frameborder="0" class="preview-media preview-iframe"></iframe>
            <div v-else class="preview-unsupported">
              <span class="material-icons" style="font-size:48px;color:#6c757d;">insert_drive_file</span>
              <p>{{ name }}</p>
              <p>No es posible previsualizar este tipo de archivo.</p>
              <button @click="downloadFile" class="btn-download">Descargar archivo</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import api from '@/api';
import Swal from 'sweetalert2';

export default {
  name: 'PreviewModal',
  emits: ['close', 'download'],
  props: {
    visible: { type: Boolean, default: false },
    file: { type: Object, default: null },
    // aceptar previewUrl directo si el padre lo pasa
    previewUrl: { type: String, default: '' }
  },
  data() {
    return { 
      _objUrl: null, 
      loading: false,
      blobUrl: null
    };
  },
  computed: {
    // devuelve la URL final (prop.previewUrl > file.preview* > blob/object/public)
    previewUrl() {
      // prioridad: prop previewUrl > file.previewUrl/preview_url/preview > blob/object/public
      if (this.previewUrl && this.previewUrl.length) return this.previewUrl;
      const f = this.file || {};
      return this.blobUrl || this._objUrl || f.previewUrl || f.preview_url || f.preview || f.url || f.file_url || '';
    },
    isImage() {
      if (!this.file && !this.previewUrl) {
        return false;
      }
      // si hay file, también comprobar tipos/mime/name
      const f = this.file || {};
      const mime = f.file?.type || f.type || f.mime_type || f.previewType || '';
      if (mime && typeof mime === 'string') return mime.startsWith('image/');
      const name = (f.file?.name || f.filename || f.name || '').toLowerCase();
      return /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(name);
    },
    isPDF() {
      // prioridad: mime/type/name/previewUrl
      if (this.previewUrl && /\.pdf($|\?)/i.test(this.previewUrl)) return true;
      const f = this.file || {};
      const mime = f.file?.type || f.type || f.mime_type || f.previewType || '';
      if (mime && typeof mime === 'string') return mime === 'application/pdf';
      const name = (f.file?.name || f.filename || f.name || '').toLowerCase();
      return /\.pdf$/.test(name);
    },
    name() {
      return this.file?.file?.name || this.file?.filename || this.file?.name || 'Archivo';
    }
  },
  methods: {
    async fetchBlobForId(id) {
      if (!id || this.blobUrl || this.loading) return;
      this.loading = true;
      try {
        // limpiar blob anterior si existiera
        if (this.blobUrl) {
          try { URL.revokeObjectURL(this.blobUrl); } catch(e) {}
          this.blobUrl = null;
        }
        const resp = await api.get(`/tickets/files/download/${id}`, { 
          responseType: 'blob' 
        });
        this.blobUrl = URL.createObjectURL(resp.data);
      } catch (e) {
        console.error('Error descargando blob para previsualizar:', e);
        Swal.fire({ 
          icon: 'error', 
          title: 'Error', 
          text: 'No se pudo previsualizar el archivo.', 
          showConfirmButton: false, 
          timer: 2500 
        });
      } finally {
        this.loading = false;
      }
    },
    async downloadFile() {
      if (!this.file) return;
      
      try {
        if (this.file.id) {
          // Archivo subido - usar API
          const response = await api.get(`/tickets/files/download/${this.file.id}`, {
            responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', this.name);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.$emit('download', this.file);
        } else if (this.file.file) {
          // Archivo local
          const url = URL.createObjectURL(this.file.file);
          const a = document.createElement('a');
          a.href = url;
          a.download = this.file.file.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.$emit('download', this.file);
        }
      } catch (error) {
        Swal.fire({ 
          icon: 'error', 
          title: 'Error', 
          text: 'No se pudo descargar el archivo.', 
          showConfirmButton: false, 
          timer: 2500 
        });
      }
    },
    onBackdropClick() {
      this.$emit('close');
    },
    cleanupUrls() {
      if (this._objUrl) {
        try { URL.revokeObjectURL(this._objUrl); } catch(e) {}
        this._objUrl = null;
      }
      if (this.blobUrl) {
        try { URL.revokeObjectURL(this.blobUrl); } catch(e) {}
        this.blobUrl = null;
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        document.body.classList.add('preview-open');
      } else {
        document.body.classList.remove('preview-open');
        this.cleanupUrls();
      }
    },
    file: {
      immediate: true,
      handler(newFile) {
        // limpiar URLs previas
        this.cleanupUrls();
        if (!newFile) return;
        // Si es archivo local, crear objectURL una sola vez
        if (newFile.file) {
          try {
            this._objUrl = URL.createObjectURL(newFile.file);
          } catch (e) {
            console.error('Error creando objectURL:', e);
          }
          return;
        }
        // Si ya tiene URL pública o previewUrl no hay que descargar nada
        if (newFile.url || newFile.file_url || newFile.previewUrl || newFile.preview_url || newFile.preview) return;
        // Si tiene id, descarga el blob de forma asíncrona y muestra cuando esté listo
        if (newFile.id) {
          this.fetchBlobForId(newFile.id);
        }
      }
    },
    // si el padre pasa la prop previewUrl directa, crear limpieza/usarla
    previewUrl(val) {
      // nada especial: computed previewUrl ya prioriza la prop; limpiar URLs si se pasa vacía
      if (!val) this.cleanupUrls();
    }
  },
  beforeUnmount() {
    this.cleanupUrls();
    document.body.classList.remove('preview-open');
  }
};
</script>

<style scoped>
/* Z-index máximo para asegurar que esté por encima de todo */
.preview-wrapper {
  position: fixed !important;
  inset: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 2147483648 !important; /* Mayor que el modal principal */
  pointer-events: auto !important;
}

.preview-backdrop {
  position: absolute !important;
  inset: 0 !important;
  background: rgba(0,0,0,0.9) !important;
  z-index: 2147483647 !important;
}

.preview-container {
  position: relative !important;
  z-index: 2147483648 !important;
  width: 100vw;
  height: 100vh;
}

/* Fullscreen modal */
.modal-content-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Ajustes para alinear botón cerrar con el título */
.modal-header {
  display: flex !important;
  align-items: center !important;     /* centra verticalmente título y botón */
  justify-content: space-between !important;
  gap: 12px !important;               /* separación entre título y botón */
  background: #99172A;
  color: #fff;
  padding: 12px 16px;
  z-index: 2;
  flex-shrink: 0;
  margin-top: 0;
  left: 0;
}

/* Título ocupa el espacio disponible y se centra verticalmente */
.modal-title {
  flex: 1 1 auto !important;
  margin: 0 !important;
  font-size: 1.1rem;
  font-weight: 400;
  display: flex;
  align-items: center;
}

/* Botón de cerrar alineado y con tamaño fijo */
.btn-close {
  margin: 0 !important;
  padding: 0 !important;
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  align-self: center !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent;
  border: none;
  cursor: pointer;
  /* bajar el aspa unos pixeles para que quede a la altura del título */
  transform: translateY(4px) !important; /* ajustar 2-6px según prefieras */
}

/* Icono dentro del botón centrado */
.btn-close .material-icons {
  font-size: 22px !important;
  line-height: 20px;
  vertical-align: middle;
  color: #ffffff !important; /* más blanca */
  text-shadow: 0 1px 0 rgba(0,0,0,0.25); /* contraste sutil para que el aspa resalte */
  /* pequeño ajuste fino si hace falta */
  display: inline-block;
  transform: translateY(1px); /* opcional, mantener alineación */
}

.modal-body {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  height: calc(100vh - 60px);
  box-sizing: border-box;
  background: #fff;
}

.preview-media {
  max-width: 90vw;
  max-height: calc(100vh - 120px);
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
}

.preview-image {
  display: block;
}

.preview-iframe {
  width: 90vw;
  height: calc(100vh - 120px);
  border: none;
  background: #fff;
}

.preview-unsupported {
  text-align: center;
  color: #6c757d;
  padding: 20px;
}

.preview-unsupported p {
  margin: 10px 0;
}

.btn-download {
  background-color: #99172A;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.btn-download:hover {
  background-color: #7A1222;
}

/* Estilos para PDF fullscreen */
.preview-container.pdf-full .modal-body {
  padding: 0;
}

.preview-container.pdf-full .preview-iframe {
  width: 100vw;
  height: calc(100vh - 60px);
  max-width: 100vw;
  max-height: calc(100vh - 60px);
}

/* Force header/body alignment y neutralizar márgenes negativos globales */
.preview-container,
.preview-header,
.preview-body,
.preview-content {
  box-sizing: border-box; /* asegurar cálculo coherente de anchuras */
}

/* evitar que un margin-top / left negativo de estilos globales desplace el header */
.preview-header {
  width: 100% !important;
  margin: 0 !important;              /* anula margin-top: -15 u otros */
  padding: 15px 20px !important;
  left: auto !important;             /* anula left: -20 */
  right: 0 !important;
  position: relative !important;
  overflow: hidden !important;       /* que no desborde */
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  transform: none !important;        /* anula transforms que creen stacking context */
}

/* forzar que el botón cerrar esté visible y dentro del header */
.preview-header .preview-close,
.preview-container .btn-close {
  position: relative !important;
  z-index: 2147483649 !important;
  margin: 0 !important;            /* anula margenes negativos */
  left: auto !important;
  top: auto !important;
  background: #99172A !important; /* rojo corporativo */
  color: #fff !important;          /* aspa blanca */
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
  /* bajar el aspa para que coincida con la línea del título */
  transform: translateY(4px) !important;
}

/* hover del botón cerrar */
.preview-header .preview-close:hover,
.preview-container .btn-close:hover {
  background: #7A1222 !important; /* más oscuro al pasar el ratón */
}

/* pequeño ajuste para que el header no sea más ancho que el body cuando hay imagen grande */
.preview-container {
  max-width: calc(100vw - 32px) !important;
  width: auto !important;
  margin: 16px !important;
  overflow: hidden !important;
}

/* asegurar que el body se ajuste al mismo ancho */
.preview-body {
  padding: 0 !important;
  width: 100% !important;
  overflow: hidden;
}
</style>

<style>
/* Estilo global para cuando el preview está abierto */
body.preview-open {
  overflow: hidden;
}
</style>